import { MigratableJson } from '../MigratableJson';

const JUSTIFY_CONTENT_MAP: Record<string, string> = {
  start: 'flex-start',
  end: 'flex-end',
};

const ALIGN_ITEMS_MAP: Record<string, string> = {
  stretch: 'flex-start',
};

/**
 * Converts an rgba() string to an 8-digit hex color.
 * e.g. "rgba(139,95,137,0.12)" → "#8B5F891F"
 */
const rgbaStringToHexa = (rgba: string): string | null => {
  const match = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)$/);
  if (!match) return null;

  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  const alphaHex = Math.round(a * 255)
    .toString(16)
    .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`.toUpperCase();
};

const migrateGradientStops = (
  gradient: Record<string, unknown>,
): Record<string, unknown> | null => {
  const stops = gradient.stops;
  if (!Array.isArray(stops)) return null;

  let changed = false;
  const newStops = stops.map((stop) => {
    if (typeof stop !== 'object' || stop === null) return stop;
    const s = stop as Record<string, unknown>;
    const hexa = s.hexa;
    if (typeof hexa !== 'string' || !hexa.startsWith('rgba')) return stop;

    const converted = rgbaStringToHexa(hexa);
    if (!converted) return stop;

    changed = true;
    return { ...s, hexa: converted };
  });

  if (!changed) return null;
  return { ...gradient, stops: newStops };
};

const migrateFlexProps = (props: Record<string, unknown>): boolean => {
  let changed = false;

  const jc = props.justifyContent;
  if (typeof jc === 'string' && jc in JUSTIFY_CONTENT_MAP) {
    props.justifyContent = JUSTIFY_CONTENT_MAP[jc];
    changed = true;
  }

  const ai = props.alignItems;
  if (typeof ai === 'string' && ai in ALIGN_ITEMS_MAP) {
    props.alignItems = ALIGN_ITEMS_MAP[ai];
    changed = true;
  }

  return changed;
};

/**
 * Migration v16: Normalize Container flex and gradient values
 *
 * The ContainerPropsDto (ENG-18599) enforces strict validation on Container nodes.
 * Existing pages may have values that predate this validation:
 *
 * - justifyContent: "start"/"end" → "flex-start"/"flex-end"
 * - alignItems: "stretch" → "flex-start"
 * - bgGradient.stops.hexa: rgba() strings → 8-digit hex colors
 * - backgroundColor: gradient objects with rgba() stops → hex stops
 */
export default function toV16<T extends MigratableJson>(data: T): T {
  const allNodes = data.getAllNodes();

  for (const { node } of allNodes) {
    const props = node.props;
    if (!props || typeof props !== 'object') continue;

    const nextProps: Record<string, unknown> = { ...props };
    let changed = false;

    // Migrate desktop-level flex props
    if (migrateFlexProps(nextProps)) {
      changed = true;
    }

    // Migrate tablet breakpoint flex props
    if (nextProps.tablet && typeof nextProps.tablet === 'object') {
      const tablet = { ...(nextProps.tablet as Record<string, unknown>) };
      if (migrateFlexProps(tablet)) {
        nextProps.tablet = tablet;
        changed = true;
      }
    }

    // Migrate mobile breakpoint flex props
    if (nextProps.mobile && typeof nextProps.mobile === 'object') {
      const mobile = { ...(nextProps.mobile as Record<string, unknown>) };
      if (migrateFlexProps(mobile)) {
        nextProps.mobile = mobile;
        changed = true;
      }
    }

    // Migrate bgGradient stops
    if (nextProps.bgGradient && typeof nextProps.bgGradient === 'object') {
      const migrated = migrateGradientStops(nextProps.bgGradient as Record<string, unknown>);
      if (migrated) {
        nextProps.bgGradient = migrated;
        changed = true;
      }
    }

    // Migrate backgroundColor when it's a gradient object with rgba stops
    if (nextProps.backgroundColor && typeof nextProps.backgroundColor === 'object') {
      const bg = nextProps.backgroundColor as Record<string, unknown>;
      if (bg.type && bg.stops) {
        const migrated = migrateGradientStops(bg);
        if (migrated) {
          nextProps.backgroundColor = migrated;
          changed = true;
        }
      }
    }

    if (changed) {
      node.props = nextProps;
    }
  }

  return data;
}
